// Feedback pins API (apps/pipeline_api/src/feedback.rs). Every route needs Preview on.
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { api } from "../api";
import type { PageContext } from "./context";
import type { Anchor } from "./anchor";

export type PinStatus = "open" | "in_progress" | "fixed" | "closed";
export const PIN_STATUSES: PinStatus[] = ["open", "in_progress", "fixed", "closed"];
/** What the page shows by default: everything not closed. */
export const OPEN_STATUSES: PinStatus[] = ["open", "in_progress", "fixed"];
export const STATUS_LABEL: Record<PinStatus, string> = { open: "Open", in_progress: "In progress", fixed: "Fixed", closed: "Closed" };

export interface Pin {
  id: string;
  number: number;
  author_id: string;
  author_name: string;
  author_role: string;
  acting_role: string | null;
  note: string;
  route: string;
  query: string;
  loan_id: string | null;
  core_loan_id: string | null;
  lo_person_id: string | null;
  anchor: Anchor;
  viewport_width: number;
  viewport_height: number;
  build_sha: string;
  api_build_sha: string;
  status: PinStatus;
  status_changed_at: string | null;
  created_at: string;
  last_activity_at: string;
  reply_count: number;
  /** A screenshot exists and this viewer may see it. */
  screenshot: boolean;
}

export interface PinEntry {
  id: string;
  author_id: string;
  author_name: string;
  author_role: string;
  acting_role: string | null;
  kind: "reply" | "status";
  body: string | null;
  status_from: PinStatus | null;
  status_to: PinStatus | null;
  created_at: string;
}

export interface PinDetail extends Pin {
  entries: PinEntry[];
  /** Older entries the API didn't return (it sends the latest 200). */
  earlier_entries: number;
  can_reply: boolean;
  next_statuses: PinStatus[];
}

export interface PinList {
  pins: Pin[];
  next: string | null;
  triage: boolean;
  api_build_sha: string;
}

export interface ListFilter {
  route?: string;
  status?: PinStatus[];
  author_id?: string;
  since?: string;
  loan_id?: string;
  core_loan_id?: string;
  lo_person_id?: string;
  subject?: "none";
}

/** Filter before pagination by the page's loan or LO. A shared page (no subject in the URL) asks for
 * every pin on the route the viewer may see: its rows carry their own loans, and a pin saved on one
 * of them is that loan's (lib/feedback/subject.ts). */
export function pageFilter(ctx: PageContext): ListFilter {
  const subject = ctx.loan_id ? { loan_id: ctx.loan_id }
    : ctx.core_loan_id ? { core_loan_id: ctx.core_loan_id }
    : ctx.lo_person_id ? { lo_person_id: ctx.lo_person_id }
    : {};
  return { route: ctx.route, status: OPEN_STATUSES, ...subject };
}

export interface NewPin {
  note: string;
  route: string;
  query: string;
  loan_id: string | null;
  core_loan_id: string | null;
  lo_person_id: string | null;
  anchor: Anchor;
  viewport: { width: number; height: number };
  build_sha: string;
  screenshot?: { media_type: string; data: string; width: number; height: number } | null;
}

export const feedbackKeys = {
  all: ["feedback"] as const,
  list: (f: ListFilter) => ["feedback", "list", f.route ?? "", (f.status ?? []).join(","), f.author_id ?? "", f.since ?? "", f.loan_id ?? "", f.core_loan_id ?? "", f.lo_person_id ?? "", f.subject ?? ""] as const,
  pin: (id: string) => ["feedback", "pin", id] as const,
};

export function listUrl(f: ListFilter, cursor?: string): string {
  const p = new URLSearchParams();
  if (f.route) p.set("route", f.route);
  if (f.status?.length) p.set("status", f.status.join(","));
  if (f.author_id) p.set("author_id", f.author_id);
  if (f.since) p.set("since", f.since);
  if (f.loan_id) p.set("loan_id", f.loan_id);
  if (f.core_loan_id) p.set("core_loan_id", f.core_loan_id);
  if (f.lo_person_id) p.set("lo_person_id", f.lo_person_id);
  if (f.subject) p.set("subject", f.subject);
  if (cursor) p.set("cursor", cursor);
  const q = p.toString();
  return q ? `/api/feedback?${q}` : "/api/feedback";
}

export function useInfiniteFeedbackList(f: ListFilter, enabled = true) {
  return useInfiniteQuery({
    queryKey: [...feedbackKeys.list(f), "pages"],
    queryFn: ({ signal, pageParam }) => api<PinList>(listUrl(f, pageParam), { signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.next ?? undefined,
    enabled,
    staleTime: 15_000,
  });
}

export function useFeedbackList(f: ListFilter, enabled = true) {
  return useQuery({
    queryKey: feedbackKeys.list(f),
    queryFn: ({ signal }) => api<PinList>(listUrl(f), { signal }),
    enabled,
    staleTime: 15_000,
  });
}

export function useFeedbackPin(id: string | null) {
  return useQuery({
    queryKey: feedbackKeys.pin(id ?? ""),
    queryFn: ({ signal }) => api<PinDetail>(`/api/feedback/${id}`, { signal }),
    enabled: !!id,
  });
}

export function screenshotUrl(id: string) {
  return `/api/feedback/${id}/screenshot`;
}

/** Create / reply / status, each refreshing every feedback query. */
export function useFeedbackActions() {
  const qc = useQueryClient();
  const refresh = useCallback(() => void qc.invalidateQueries({ queryKey: feedbackKeys.all }), [qc]);
  const create = useCallback(
    async (body: NewPin) => {
      const d = await api<PinDetail>("/api/feedback", { method: "POST", body });
      qc.setQueryData(feedbackKeys.pin(d.id), d);
      refresh();
      return d;
    },
    [qc, refresh],
  );
  const reply = useCallback(
    async (id: string, text: string) => {
      const d = await api<PinDetail>(`/api/feedback/${id}/replies`, { method: "POST", body: { body: text } });
      qc.setQueryData(feedbackKeys.pin(id), d);
      refresh();
      return d;
    },
    [qc, refresh],
  );
  const setStatus = useCallback(
    async (id: string, status: PinStatus) => {
      const d = await api<PinDetail>(`/api/feedback/${id}/status`, { method: "POST", body: { status } });
      qc.setQueryData(feedbackKeys.pin(id), d);
      refresh();
      return d;
    },
    [qc, refresh],
  );
  return { create, reply, setStatus };
}
